import { GiveawayStockStatus, type GiveawayStockStatusType } from '@cart-services/core/couponService'
import { type MergedClaimsCoupon } from '@cart-services/types'
import { wordpressCartGiveawayEvents } from '@dni-analysis'
import { type AvailableCoupon } from '@hooks-api/types/dataModel/coupon'
import { Chip } from '@pets-ui/Chip/Chip'
import { HStack } from '@pets-ui/HStack/HStack'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { ArrowRightSmall } from '@pets-ui/Icons'
import { Text } from '@pets-ui/Text/Text'
import { zIndex } from '@pets-ui/tokens/zIndex.stylex'
import { Tooltip } from '@pets-ui/Tooltip/Tooltip'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { formatPrice } from '@utils'
import { useCallback, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import cartUiI18nInstance from '../i18n/i18nConfig'
import { CouponConditionComposedLabel } from './CouponConditionComposedLabel'
import { CouponConditionLabel } from './CouponConditionLabel'
import { CouponConditionPrefixIcon } from './CouponConditionPrefixIcon'
import { CouponConditionPrefixText } from './CouponConditionPrefixText'
import { CouponConditionWrapper } from './CouponConditionWrapper'

export interface TooltipInfoProps {
  criterionAmount: number
  criterionQuantity: number
  giftSelectionCount: number
}

export interface CouponConditionProps {
  displayName?: ReactNode
  isApplied?: boolean
  currency?: string
  moreAmount?: number | null
  moreQuantity?: number | null
  count?: number | null
  useCssGrid?: boolean
  shouldComposedLabel?: boolean
  coupon?: AvailableCoupon | MergedClaimsCoupon
  showToPromotionButton?: boolean
  isVisitor?: boolean
  showGiveawayTooltip?: boolean
  tooltipInfo?: TooltipInfoProps
  isRepeatable?: boolean
  giveawayStockStatus?: GiveawayStockStatusType
}

interface CouponConditionPrefixProps {
  isApplied?: CouponConditionProps['isApplied']
  moreAmount?: CouponConditionProps['moreAmount']
  moreQuantity?: CouponConditionProps['moreQuantity']
  currency?: CouponConditionProps['currency']
  count?: CouponConditionProps['count']
  isVisitor?: CouponConditionProps['isVisitor']
  isRepeatable?: CouponConditionProps['isRepeatable']
}

const CouponConditionPrefix = (props: CouponConditionPrefixProps) => {
  const { isApplied, moreAmount, moreQuantity, currency, count, isVisitor, isRepeatable } = props

  return (
    <HStack gap={1}>
      <CouponConditionPrefixIcon isApplied={isApplied} moreAmount={moreAmount} moreQuantity={moreQuantity} />
      {!isVisitor && (
        <CouponConditionPrefixText
          isApplied={isApplied}
          moreAmount={moreAmount}
          moreQuantity={moreQuantity}
          currency={currency}
          count={count}
          isRepeatable={isRepeatable}
        />
      )}
    </HStack>
  )
}

interface GiveawayTooltipTitleProps {
  currency?: string
  tooltipInfo: TooltipInfoProps
}

const GiveawayTooltipTitle = (props: GiveawayTooltipTitleProps) => {
  const { currency, tooltipInfo } = props
  const { criterionQuantity, criterionAmount, giftSelectionCount } = tooltipInfo
  const criterion = criterionQuantity || formatPrice(currency, criterionAmount)
  const criterionDouble = criterionQuantity ? criterionQuantity * 2 : formatPrice(currency, criterionAmount * 2)
  const giftSelectionCountDouble = giftSelectionCount * 2
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })

  return (
    <HStack>
      <Text>
        {t('cartUi.giveawayTooltipTitle', {
          criterion,
          giftSelectionCount,
          criterionDouble,
          giftSelectionCountDouble,
          defaultValue: '買 {{criterion}} 送 {{giftSelectionCount}}、買 {{criterionDouble}} 送 {{giftSelectionCountDouble}}.. 以此類推',
        })}
      </Text>
    </HStack>
  )
}

export const CouponCondition = (props: CouponConditionProps) => {
  const { displayName, isApplied, currency, moreAmount, moreQuantity, count, useCssGrid, shouldComposedLabel, showToPromotionButton = false, coupon, isVisitor = false, showGiveawayTooltip = false, tooltipInfo, isRepeatable, giveawayStockStatus = GiveawayStockStatus.AVAILABLE } = props
  const isMobile = useResponsive('mobile')
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const { sendClickAccumulatedQualified } = wordpressCartGiveawayEvents()
  const isAllGiveawaySoldOut = giveawayStockStatus === GiveawayStockStatus.ALL_SOLD_OUT
  const handleOpenSendEvent = useCallback(() => {
    if (isMobile) {
      sendClickAccumulatedQualified()
    }
  }, [isMobile, sendClickAccumulatedQualified])

  return (
    <CouponConditionWrapper useCssGrid={useCssGrid}>
      {/* NOTE: 為了讓 mobile 點擊 tooltip 不會出發父層 a 標籤的 click 事件 */}
      <div onClick={(e) => {
        if (!showGiveawayTooltip) return
        e.preventDefault()
        e.stopPropagation()
      }}
      >
        {showGiveawayTooltip && tooltipInfo
          ? (
            <Tooltip
              onOpenSendEvent={handleOpenSendEvent}
              PaperProps={{ maxWidth: '100%' }}
              variant="dark"
              title={<GiveawayTooltipTitle currency={currency} tooltipInfo={tooltipInfo} />}
            >
              <HStack zIndex={zIndex.tooltip}>
                <CouponConditionPrefix
                  isApplied={isApplied}
                  moreAmount={moreAmount}
                  moreQuantity={moreQuantity}
                  currency={currency}
                  count={count}
                  isVisitor={isVisitor}
                  isRepeatable={isRepeatable}
                />
              </HStack>
            </Tooltip>
          )
          : <CouponConditionPrefix isApplied={isApplied} moreAmount={moreAmount} moreQuantity={moreQuantity} currency={currency} count={count} isVisitor={isVisitor} />}
      </div>
      {shouldComposedLabel && coupon ? <CouponConditionComposedLabel coupon={coupon} currency={currency} /> : <CouponConditionLabel displayName={displayName} />}
      {/* NOTE: 此格永遠 render 以佔住 css grid 第三欄；贈品全贈完時箭頭消失不可點 */}
      <HStack gap={1} alignItems="center">
        {giveawayStockStatus === GiveawayStockStatus.PARTIALLY_SOLD_OUT && (
          <Chip variant="square" size="xSmall" color="blueGrey">{t('cartUi.partiallyGivenAway', { defaultValue: '部分贈完' })}</Chip>
        )}
        {isAllGiveawaySoldOut && (
          <Chip variant="square" size="xSmall" color="blueGrey">{t('cartUi.allGivenAway', { defaultValue: '已贈完' })}</Chip>
        )}
        {showToPromotionButton && !isAllGiveawaySoldOut && (
          <IconButton fontSize={16} color="grey">
            <ArrowRightSmall />
          </IconButton>
        )}
      </HStack>
    </CouponConditionWrapper>
  )
}
